import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useClaraVoice, type ClaraTurnStep } from '../contexts/ClaraVoiceContext'
import { EnvSwitchPanel } from './EnvSwitcher'
import {
  FiChevronLeft, FiLogOut, FiMenu, FiSettings, FiRepeat,
  FiUsers, FiSmartphone, FiArrowLeft, FiChevronDown, FiChevronRight,
  FiClock, FiMapPin, FiMessageSquare, FiTrendingUp, FiSlash, FiX,
  FiMoon, FiSun, FiZap,
} from 'react-icons/fi'
import { ClaraOrb, type ClaraOrbMode } from './ClaraOrb'
// MetaIcon vem do DevicesPanel, que já exporta pra uso externo — é o mesmo
// ícone que o crm.twms usa no filho 'Meta ADS'.
import { MetaIcon } from './DevicesPanel'
import { LuUserPlus } from 'react-icons/lu'
import { BsFillHddRackFill, BsFolderFill, BsAwardFill } from 'react-icons/bs'
import VerifyRoundIcon from '@rsuite/icons/VerifyRound'
import ReadyRoundIcon from '@rsuite/icons/ReadyRound'
import DraftRoundIcon from '@rsuite/icons/DraftRound'

// Sidebar lateral do CRM — substitui o header horizontal (AppHeader). Mesmo padrão
// do fin.twms (ver vault: conhecimento/sidebar-layout.md): escura fixa, colapsável,
// drawer no mobile, painéis de troca-de-ambiente e Configurações revelados no
// espaço livre (sem popover → sem briga de z-index com o #root).

export type AppView =
  | 'whatsapp' | 'map' | 'infrastructure' | 'arquivos'
  | 'marketing' | 'marketing-agente' | 'aplicativo' | 'sistema' | 'bloqueados'
  | 'lideres-acompanhamento' | 'lideres-cobertura' | 'lideres-captacao' | 'lideres-pareto'

type NavIcon = (props: { className?: string }) => JSX.Element

// Badges de "novo"/"atualizado" — mesma mecânica do fin.twms (ver FinSidebar.tsx
// lá na stack fin-twms): aparecem sozinhas a partir da data configurada e somem
// POR USUÁRIO quando ele abre o painel (regra "some quando vê", pedido do dono
// 2026-07-10: se o Cleverson abriu, continua aparecendo pro Ricardo até ele
// abrir também). O per-usuário é o localStorage do navegador de cada um — sem
// backend. Pra marcar uma tela como nova ou recém-atualizada: só
// adicionar/trocar a entrada abaixo com a data de hoje + a lista do que mudou
// (`mudancas`). Chave solta (string) pra também aceitar o id do grupo
// "Recrutamento" (não é um AppView navegável, só o cabeçalho do grupo).
//
// Dinâmico com o espaço da sidebar: só um badge circular colorido (verde=novo,
// âmbar=atualizado) junto do ícone, antes do título — nada de rótulo de texto
// competindo por espaço com o nome da tela. Passar o mouse no badge mostra a
// lista do que mudou.
const BADGE_CONFIG: Partial<Record<string, { tipo: 'novo' | 'atualizado'; desde: string; mudancas: string[] }>> = {
  whatsapp: {
    tipo: 'atualizado', desde: '2026-07-14',
    mudancas: [
      '"WhatsApp Meta" agora se chama "Conversas WhatsApp", com o mesmo ícone de balão do fin.twms — nome e ícone padronizados entre os apps',
    ],
  },
  map: {
    tipo: 'atualizado', desde: '2026-07-14',
    mudancas: [
      'Painel de controles (busca, semanas, camadas) agora fecha no X — vira a pílula "Controles" no canto e libera a visão do mapa (pedido da diretoria)',
      'Camada "Organização (líder real)" agora vem ligada por padrão — clicar numa líder já isola ela + suas consultoras com zoom, igual ao painel Rede',
      'Card de detalhes redesenhado no padrão visual do painel Rede (painel flutuante no lugar do balão do mapa)',
      'Card mostra a ativação em detalhe: vendas por semana da vitrine, nº de pedidos e data do último pedido (direto do Oracle)',
      'Card da líder lista a equipe do grupo: quem ativou (com valor vendido) e as inativas (com a última semana em que ativaram)',
      'Card da empresária mostra a estrutura do distrito: as líderes abaixo dela (com equipe e valores), consultoras sem líder ativa e inativas',
      'Foco da líder mais evidente: mapa fica fosco e as linhas mostram a equipe completa da vitrine (não só a semana selecionada)',
    ],
  },
  arquivos: {
    tipo: 'atualizado', desde: '2026-07-14',
    mudancas: [
      'Busca por nome: digite no campo do topo e Enter — varre a pasta atual e todas as subpastas (acentos e maiúsculas não importam)',
      'Resultado mostra o caminho de cada item; clique numa pasta pra navegar até ela, ou no arquivo pra abrir/baixar direto',
    ],
  },
  recrutamento: {
    tipo: 'atualizado', desde: '2026-07-16',
    mudancas: [
      'Novo subpainel "Bloqueados": bloqueie um contato golpista e ele deixa de ser atendido pela Jú e nunca mais é encaminhado para uma líder — nem pelo automático, nem pela atribuição manual',
      'As mensagens de quem está bloqueado continuam chegando na inbox, marcadas em vermelho: a operação vê que ele voltou, mas nenhuma líder é exposta',
      'A lista guarda o motivo, quem bloqueou e quantas vezes o contato tentou voltar depois de bloqueado',
    ],
  },
  'lideres-pareto': {
    tipo: 'novo', desde: '2026-07-14',
    mudancas: [
      'Ranking Pareto: quem concentra as vendas, o recrutamento e a atividade WhatsApp do ano',
      'Cada linha tem o WhatsApp da líder/empresária — a lista é pra conversar com esses contatos',
      'Filtro "Só o corte 80/20" e ordenação por vendas, recrutas ou WhatsApp',
    ],
  },
}

interface BadgeInfo { dotClass: string; tooltip: string }

// "Visto" por usuário via localStorage. A chave inclui o `desde`: uma
// atualização NOVA (data diferente) re-exibe o badge mesmo pra quem já tinha
// visto a anterior. try/catch cobre storage bloqueado (navegação privada etc).
const badgeSeenKey = (view: string, desde: string) => `badge_seen:${view}:${desde}`

function badgeVisto(view: string): boolean {
  const cfg = BADGE_CONFIG[view]
  if (!cfg) return false
  try { return localStorage.getItem(badgeSeenKey(view, cfg.desde)) !== null } catch { return false }
}

// Grava o "visto" se houver badge pendente pro view. Retorna true se algo
// mudou — sinal pro chamador forçar re-render (localStorage não é reativo).
function marcarBadgeVisto(view: string): boolean {
  const cfg = BADGE_CONFIG[view]
  if (!cfg || badgeVisto(view)) return false
  try { localStorage.setItem(badgeSeenKey(view, cfg.desde), new Date().toISOString()) } catch { return false }
  return true
}

function badgeAtivo(view: string): BadgeInfo | null {
  const cfg = BADGE_CONFIG[view]
  if (!cfg) return null
  const desdeMs = new Date(`${cfg.desde}T00:00:00`).getTime()
  // Só a partir da data de estreia; daí em diante fica ATÉ este usuário abrir
  // o painel (sem corte de dias — regra "some quando vê").
  if (Date.now() < desdeMs) return null
  if (badgeVisto(view)) return null
  const isNovo = cfg.tipo === 'novo'
  const dataFmt = new Date(desdeMs).toLocaleDateString('pt-BR')
  return {
    dotClass: isNovo ? 'bg-emerald-500' : 'bg-amber-500',
    tooltip: `${isNovo ? 'Novidade' : 'Atualizado'} em ${dataFmt}:\n${cfg.mudancas.map(m => `• ${m}`).join('\n')}`,
  }
}

// Wrappers pros ícones @rsuite (renderizam <svg> que aceita className; cor via currentColor).
const MapaIcon: NavIcon      = ({ className }) => <DraftRoundIcon className={className} />
const MarketingIcon: NavIcon = ({ className }) => <ReadyRoundIcon className={className} />
const SistemaIcon: NavIcon   = ({ className }) => <VerifyRoundIcon className={className} />

interface NavLeaf { id: AppView; icon: NavIcon; label: string; tool?: string; adminOnly?: boolean; needsFolderAccess?: boolean }
interface NavGroup { id: string; icon: NavIcon; label: string; tool?: string; children: NavLeaf[] }

const NAV_ITEMS: NavLeaf[] = [
  // Nome e ícone padronizados com o fin.twms (FiMessageSquare / "Conversas
  // WhatsApp") — o SVG do Meta (∞) confundia na sidebar (pedido 2026-07-14).
  { id: 'whatsapp',       icon: FiMessageSquare as NavIcon,   label: 'Conversas WhatsApp', tool: 'whatsapp' },
  { id: 'map',            icon: MapaIcon,                     label: 'Mapa de Ativas', tool: 'mapa' },
  { id: 'infrastructure', icon: BsFillHddRackFill as NavIcon, label: 'Dados', tool: 'infrastructure' },
  { id: 'arquivos',       icon: BsFolderFill as NavIcon,      label: 'Arquivos',   needsFolderAccess: true },
  // O app Luísa (28/09/2026): acessos, pessoas e a revista do app das consultoras.
  { id: 'aplicativo',     icon: FiSmartphone as NavIcon,      label: 'Aplicativo', tool: 'aplicativo' },
  { id: 'sistema',        icon: SistemaIcon,                  label: 'Tecnologia', tool: 'sistema' },
]

// Grupo "Recrutamento" (nível 2) — Acompanhamento/Relatório/Cobertura eram abas
// dentro do painel; viraram subpainéis próprios na sidebar (pedido 2026-07-06).
// Em 2026-07-10 o Relatório foi fundido no Acompanhamento (mesma árvore
// distrito→grupo→líder duplicada; o export PDF/cidade/ociosas migraram pra lá).
// Em 2026-07-14 a Captação (landing "Quero ser Consultora") migrou do Marketing
// pra cá, e "Rede" virou "Mapa de Rede".
// Em 2026-07-16 "Bloqueados" entrou aqui: o golpista bloqueado é justamente um
// lead de recrutamento que não pode chegar a líder nenhuma.
// Grupo "Marketing" — o mesmo do crm.twms, com os mesmos ícones: dentro ficam o
// "Meta ADS" (as campanhas de tráfego, ainda sem integração nesta praça, e os
// disparos que já foram feitos) e "Campanhas" (o Agente de Campanha). O grupo
// herda a tool 'blast' que a folha solta tinha: quem via Marketing continua
// vendo os dois filhos. Recolhida, a barra navega no 1º filho — o Meta ADS, que
// é exatamente o painel que a folha abria antes.
const MARKETING: NavGroup = {
  id: 'marketing-grupo', icon: MarketingIcon, label: 'Marketing', tool: 'blast',
  children: [
    { id: 'marketing',        icon: MetaIcon as NavIcon, label: 'Meta ADS' },
    { id: 'marketing-agente', icon: FiZap as NavIcon,    label: 'Campanhas' },
  ],
}

const RECRUTAMENTO: NavGroup = {
  id: 'recrutamento', icon: BsAwardFill as NavIcon, label: 'Recrutamento', tool: 'leaders',
  children: [
    { id: 'lideres-acompanhamento', icon: FiClock as NavIcon,  label: 'Acompanhamento' },
    { id: 'lideres-cobertura',      icon: FiMapPin as NavIcon, label: 'Mapa de Rede' },
    { id: 'lideres-captacao',       icon: FiUsers as NavIcon,  label: 'Captação' },
    { id: 'lideres-pareto',         icon: FiTrendingUp as NavIcon, label: 'Pareto' },
    // adminOnly: o grupo abre com a tool 'leaders' (concedível a não-admin),
    // mas bloquear tira o contato do atendimento e do encaminhamento — decisão
    // de diretoria. Primeiro filho com gate próprio: o render dos children
    // filtra `adminOnly` por causa dele.
    { id: 'bloqueados',             icon: FiSlash as NavIcon,  label: 'Bloqueados', adminOnly: true },
  ],
}

// ── Liberação incremental de painéis (praça nova) ──────────────────────────
//
// Praça que entra no ar aos poucos mostra a sidebar INTEIRA, mas só deixa clicar
// no que já foi conferido; o resto fica cinza com "em preparação". É diferente
// de permissão: `hasToolAccess` ESCONDE o item (o usuário não pode saber que a
// tela existe), aqui a intenção é o oposto — o cliente vê o que ainda vem.
//
// Vem de env de build (`VITE_PAINEIS_LIBERADOS=map,infrastructure`) e não de
// constante no código de propósito: assim a matriz e as praças rodam o MESMO
// arquivo, e liberar mais um painel é editar o .env da praça + rebuild, sem
// tocar em TSX nem criar divergência de código entre elas.
//
// AUSENTE ou vazio = tudo liberado. É o que mantém a matriz (e qualquer stack
// que não conheça essa variável) funcionando exatamente como antes.
const PAINEIS_LIBERADOS: Set<string> | null = (() => {
  const raw = (import.meta.env?.VITE_PAINEIS_LIBERADOS ?? '').trim()
  if (!raw) return null
  return new Set(raw.split(',').map((s: string) => s.trim()).filter(Boolean))
})()

// Todos os grupos, na ordem em que a barra desenha. Os ajudantes abaixo passaram
// a varrer isto em vez de citar o RECRUTAMENTO pelo nome — senão cada grupo novo
// exigiria lembrar de cinco lugares, e esquecer um deles vira furo de deep-link.
const GRUPOS: NavGroup[] = [MARKETING, RECRUTAMENTO]
const FILHOS = GRUPOS.flatMap(g => g.children.map(c => ({ ...c, grupo: g })))

// A árvore da navegação, NA ORDEM em que a barra desenha: as folhas até
// "Arquivos", o grupo Marketing, "Tecnologia" e o grupo Recrutamento. É a mesma
// ordem do crm.twms.
type NavEntry = (typeof NAV_ITEMS)[number] | NavGroup
const ehGrupo = (e: NavEntry): e is NavGroup => Array.isArray((e as NavGroup).children)
const NAV_TREE: NavEntry[] = [
  ...NAV_ITEMS.filter(n => n.id !== 'sistema'),
  MARKETING,
  ...NAV_ITEMS.filter(n => n.id === 'sistema'),
  RECRUTAMENTO,
]

// `true` = clicável. Painel fora do menu (tela interna) nunca é bloqueado aqui.
export function painelLiberado(v: AppView): boolean {
  if (!PAINEIS_LIBERADOS) return true
  const noMenu = NAV_ITEMS.some(n => n.id === v) || FILHOS.some(c => c.id === v)
  if (!noMenu) return true
  return PAINEIS_LIBERADOS.has(v)
}

// Primeiro painel liberado, na ordem do menu — SEM depender de permissão, pra
// poder ser usado no `useState` inicial do App (onde `user` ainda não resolveu).
// Só decide liberação; quem checa permissão continua sendo o guard do App.
export function primeiroPainelLiberado(): AppView {
  const todos: AppView[] = [...NAV_ITEMS.map(n => n.id), ...FILHOS.map(c => c.id)]
  return todos.find(painelLiberado) ?? 'whatsapp'
}

// Quem pode ver cada painel. MESMA fonte que filtra a sidebar (NAV_ITEMS +
// RECRUTAMENTO) — de propósito: é uma regra só, num lugar só.
//
// Existe porque o deep-link entra DIRETO no viewMode: `?panel=bloqueados` não
// passa pela sidebar e portanto não passava por filtro nenhum. Hoje o backend
// segura (as rotas de Bloqueados exigem admin), mas isso faz a proteção do
// painel depender de cada rota nova lembrar do middleware — e painel novo com
// rota esquecida vira exposição por URL. Aqui a regra vale mesmo que uma rota
// falhe. `hasToolAccess` já dá bypass a admin (ver AuthContext).
export function canViewPanel(
  v: AppView,
  perms: { isAdmin: boolean; hasToolAccess: (t: string) => boolean; hasFolderAccess: boolean },
): boolean {
  const filho = FILHOS.find(c => c.id === v)
  const item = NAV_ITEMS.find(n => n.id === v) ?? filho
  // View que não está no menu não se decide aqui (não inventar bloqueio pra
  // tela interna que nunca foi item de navegação).
  if (!item) return true
  // Painel ainda não liberado nesta praça: cinza na sidebar E fechado por URL.
  // Sem esta linha, `?panel=marketing` entraria direto no painel desabilitado —
  // é exatamente o furo de deep-link que o comentário acima descreve.
  if (!painelLiberado(v)) return false
  if (item.adminOnly && !perms.isAdmin) return false
  if (item.needsFolderAccess && !perms.isAdmin && !perms.hasFolderAccess) return false
  // Filho do grupo exige TAMBÉM a tool do grupo — na sidebar isso é implícito
  // (o grupo inteiro some); no deep-link precisa ser explícito.
  if (filho && filho.grupo.tool && !perms.hasToolAccess(filho.grupo.tool)) return false
  if (item.tool && !perms.hasToolAccess(item.tool)) return false
  return true
}

// Primeiro painel que ESTE usuário pode ver, na ordem do menu. Serve de destino
// quando um deep-link é barrado: mandar pro 'whatsapp' fixo cairia num painel
// igualmente bloqueado pra quem não tem essa tool.
export function firstViewablePanel(
  perms: { isAdmin: boolean; hasToolAccess: (t: string) => boolean; hasFolderAccess: boolean },
): AppView {
  const todos: AppView[] = [...NAV_ITEMS.map(n => n.id), ...FILHOS.map(c => c.id)]
  // Último recurso: usuário sem permissão pra NENHUM painel liberado. O
  // 'whatsapp' fixo que estava aqui é justamente um painel bloqueado na praça em
  // liberação — deixaria a tela num painel que a sidebar mostra em cinza.
  return todos.find(v => canViewPanel(v, perms)) ?? primeiroPainelLiberado()
}

// Rótulo legível por view — usado pelo mini-header mobile do App.
export function crmViewLabel(v: AppView): string {
  return NAV_ITEMS.find(n => n.id === v)?.label ?? FILHOS.find(c => c.id === v)?.label ?? 'TWMS'
}

const EASE = '[transition-timing-function:cubic-bezier(0.16,1,0.3,1)]'

export type SettingsAction = 'users' | 'new-user' | 'new-device'

// Painel de Configurações (admin) — mesma revelação do swap, ações administrativas.
// `my-2` no cartão em vez de `pt-2` no wrapper: mesma razão do EnvSwitchPanel
// (comentário longo em EnvSwitcher.tsx) — o `.neu-side-pop` não tem cor nem
// borda próprias, então sem folga em cima o `overflow-hidden` deceparia a
// aresta de luz e o topo do cartão sumiria dentro da barra.
function SettingsRevealPanel({ open, onClose, collapsed, onAction }: {
  open: boolean; onClose: () => void; collapsed: boolean; onAction: (a: SettingsAction) => void
}) {
  const items: Array<{ a: SettingsAction; icon: NavIcon; label: string; hint: string; color: string }> = [
    { a: 'users',      icon: FiUsers as NavIcon,      label: 'Usuários e Permissões', hint: 'Gerenciar acessos',      color: 'text-violet-500' },
    { a: 'new-user',   icon: LuUserPlus as NavIcon,   label: 'Novo Usuário',          hint: 'Criar conta de acesso',  color: 'text-emerald-500' },
    { a: 'new-device', icon: FiSmartphone as NavIcon, label: 'Novo Dispositivo Meta', hint: 'Conectar WhatsApp Business', color: 'text-blue-500' },
  ]
  return (
    <div
      aria-hidden={!open}
      className={['flex min-h-0 flex-col overflow-hidden px-2 transition-[max-height] duration-[650ms]', EASE,
        open ? 'max-h-full' : 'max-h-0 pointer-events-none'].join(' ')}
    >
      <div
        style={{ transitionDelay: open ? '40ms' : '0ms' }}
        className={['flex min-h-0 flex-col my-2 rounded-xl border border-border bg-secondary/30 neu-side-pop p-1.5 origin-bottom will-change-transform transition-[transform,opacity,box-shadow] duration-[600ms]', EASE,
          open ? 'translate-y-0 scale-100 opacity-100 shadow-lg shadow-black/25' : 'translate-y-3 scale-[0.97] opacity-0'].join(' ')}
      >
        <button
          onClick={onClose}
          title="Voltar"
          className={`group shrink-0 flex items-center gap-1.5 rounded-lg px-1.5 py-1 w-full text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors ${collapsed ? 'justify-center' : ''}`}
        >
          <FiArrowLeft className="w-4 h-4 shrink-0" />
          {!collapsed && <span>Administração</span>}
        </button>
        <div className="mt-1 space-y-1 min-h-0 overflow-y-auto">
          {items.map(({ a, icon: Icon, label, hint, color }, i) => (
            <button
              key={a}
              onClick={() => { onClose(); onAction(a) }}
              title={collapsed ? label : undefined}
              style={{ transitionDelay: open ? `${130 + i * 70}ms` : '0ms' }}
              className={['w-full flex items-center gap-2.5 rounded-lg border border-border p-2 text-left neu-side-pop-item will-change-transform transition-[opacity,transform,border-color,background-color] duration-[500ms] hover:border-primary/40 hover:bg-secondary', EASE,
                collapsed ? 'justify-center' : '',
                open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'].join(' ')}
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 bg-secondary neu-side-well">
                <Icon className={`w-4 h-4 ${color}`} />
              </span>
              {!collapsed && (
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block text-[13px] font-medium text-foreground truncate">{label}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">{hint}</span>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// Painel da Clara — timeline do turno de voz, revelada no espaço livre acima
// do rodapé (mesma mecânica do SettingsRevealPanel/EnvSwitchPanel: sobe de
// baixo). Abre sozinho quando um comando é identificado (evento `transcript`
// do clara-wake, pós-guardrail) e recolhe sozinho ao fim do turno.
// `my-2` no cartão em vez de `pt-2` no wrapper: mesma razão do EnvSwitchPanel
// (comentário longo em EnvSwitcher.tsx) — o `.neu-side-pop` não tem cor nem
// borda próprias, então sem folga em cima o `overflow-hidden` deceparia a
// aresta de luz e o topo do cartão sumiria dentro da barra.
function ClaraTurnPanel({ open, steps, onClose }: {
  open: boolean; steps: ClaraTurnStep[]; onClose: () => void
}) {
  // Etapa nova entra fora da área visível quando a lista cresce — rola pro fim.
  const listRef = useRef<HTMLOListElement>(null)
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [steps])
  return (
    <div
      aria-hidden={!open}
      className={['flex min-h-0 flex-col overflow-hidden px-2 transition-[max-height] duration-[650ms]', EASE,
        open ? 'max-h-full' : 'max-h-0 pointer-events-none'].join(' ')}
    >
      <div
        style={{ transitionDelay: open ? '40ms' : '0ms' }}
        className={['flex min-h-0 flex-col my-2 rounded-xl border border-border bg-secondary/30 neu-side-pop p-2 origin-bottom will-change-transform transition-[transform,opacity,box-shadow] duration-[600ms]', EASE,
          open ? 'translate-y-0 scale-100 opacity-100 shadow-lg shadow-black/25' : 'translate-y-3 scale-[0.97] opacity-0'].join(' ')}
      >
        <div className="shrink-0 flex items-center gap-1.5 px-1 pb-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex-1">Clara</span>
          <button
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar painel da Clara"
            className="flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <FiX className="w-3.5 h-3.5" />
          </button>
        </div>
        <ol ref={listRef} className="min-h-0 overflow-y-auto px-1 pb-0.5">
          {steps.map((st, i) => {
            const last = i === steps.length - 1
            const dot =
              st.status === 'doing' ? 'bg-primary animate-pulse ring-4 ring-primary/20'
              : st.status === 'error' ? 'bg-red-500'
              : 'bg-primary'
            return (
              <li key={st.id} className="relative pl-5 pb-2.5 last:pb-0">
                {!last && <span aria-hidden className="absolute left-[4.5px] top-3.5 bottom-0 w-px bg-border" />}
                <span aria-hidden className={`absolute left-0.5 top-[5px] w-2 h-2 rounded-full ${dot}`} />
                <span className={`block text-[12px] font-medium leading-tight ${st.status === 'error' ? 'text-red-500' : 'text-foreground'}`}>
                  {st.title}{st.status === 'doing' ? '…' : ''}
                </span>
                {st.detail && (
                  <span title={st.detail} className={`block mt-0.5 text-[11px] leading-snug text-muted-foreground ${st.full ? '' : 'line-clamp-3'}`}>
                    {st.detail}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}

interface CrmSidebarProps {
  viewMode: AppView
  onNavigate: (v: AppView) => void
  collapsed: boolean
  onToggleCollapse: () => void
  mobileOpen: boolean
  onCloseMobile: () => void
  onSettingsAction: (a: SettingsAction) => void
}

export function CrmSidebar({ viewMode, onNavigate, collapsed, onToggleCollapse, mobileOpen, onCloseMobile, onSettingsAction }: CrmSidebarProps) {
  // A faixa "CRM | CENTRAL DE RELACIONAMENTO E MARKETING" saiu em 07/08/2026:
  // com a sidebar descolada ela só poluía a placa. Junto foram o filete que a
  // separava do rodapé e todo o maquinário de font-size dinâmico (ref +
  // useLayoutEffect + ResizeObserver) que existia só pra encaixar aquele texto
  // na largura. Mesmo corte feito no fin e no cob.
  const { user, logout, hasToolAccess, hasFolderAccess } = useAuth()
  const { theme, setTheme } = useTheme()
  const claraVoice = useClaraVoice()
  const [envOpen, setEnvOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // Nasce RETRAÍDO (07/08/2026): abrir o grupo por padrão só enchia a barra.
  // A exceção é entrar já dentro de um filho de Recrutamento (deep link / última
  // tela): com o grupo fechado os filhos nem são renderizados, então NADA na
  // navegação apareceria como ativo e a barra mentiria sobre onde você está.
  // Um mapa em vez de um booleano por grupo: com dois grupos, `xOpen`/`setXOpen`
  // duplicado é como se esquece de abrir o segundo quando se entra por deep link.
  const [abertos, setAbertos] = useState<Record<string, boolean>>(
    () => Object.fromEntries(GRUPOS.map(g => [g.id, g.children.some(c => c.id === viewMode)])))
  const alterna = (id: string) => setAbertos(m => ({ ...m, [id]: !m[id] }))

  // Painel da Clara divide o mesmo espaço livre dos painéis de admin/swap —
  // quando um comando abre a timeline, os outros dois recolhem.
  const claraPanelOpen = claraVoice.turnOpen && !collapsed
  useEffect(() => {
    if (claraPanelOpen) { setSettingsOpen(false); setEnvOpen(false) }
  }, [claraPanelOpen])

  // Marca o painel ativo como visto — cobre o clique de navegação E o view
  // default de abertura do app num lugar só (o effect roda sempre que
  // `viewMode` muda, inclusive no mount). O tick força re-render pro badge
  // sumir na hora, já que localStorage não é reativo.
  const [, setSeenTick] = useState(0)
  useEffect(() => {
    let mudou = marcarBadgeVisto(viewMode)
    // O badge do Recrutamento é uma entrada única na chave do GRUPO (não há
    // entradas por filho): abrir qualquer subpainel do grupo conta como "ver"
    // a novidade dele.
    for (const g of GRUPOS) if (g.children.some(c => c.id === viewMode)) {
      mudou = marcarBadgeVisto(g.id) || mudou
    }
    if (mudou) setSeenTick(t => t + 1)
  }, [viewMode])

  const userLabel = user?.fullName || user?.username || ''
  const avatarLetter = (user?.fullName || user?.username || '?').charAt(0).toUpperCase()

  const visibleNavItems = NAV_ITEMS.filter(item =>
    (!item.tool || hasToolAccess(item.tool)) &&
    (!item.adminOnly || !!user?.isAdmin) &&
    (!item.needsFolderAccess || !!user?.isAdmin || hasFolderAccess)
  )
  // A tool do grupo abre o grupo pra não-admin; filho com adminOnly (Bloqueados)
  // precisa do gate próprio — o grupo ser visível não o libera.
  const filhosDe = (g: NavGroup) => g.children.filter(c => !c.adminOnly || !!user?.isAdmin)
  const grupoVisivel = (g: NavGroup) => !g.tool || hasToolAccess(g.tool)

  // "Home" da marca: era 'whatsapp' fixo, o que numa praça em liberação
  // incremental clica direto num painel bloqueado (o guard do App rebateria).
  // Primeiro painel visível E liberado, na ordem do menu.
  const homeView: AppView =
    visibleNavItems.find(n => painelLiberado(n.id))?.id ??
    GRUPOS.flatMap(filhosDe).find(c => painelLiberado(c.id))?.id ??
    'whatsapp'

  const go = (v: AppView) => { onNavigate(v); onCloseMobile(); setEnvOpen(false); setSettingsOpen(false) }

  // Item de navegação é <a href> de verdade, não <button>: o App já reflete o
  // painel na URL (?panel=), então cada item TEM endereço — só faltava o
  // navegador saber disso. Com o link, o menu do botão direito passa a oferecer
  // "abrir em nova aba/janela" e Ctrl+clique / clique do meio funcionam sozinhos
  // (pedido do diretor, 2026-07-21). O clique normal continua sendo SPA.
  const hrefFor = (v: AppView) => `${window.location.pathname}?panel=${v}`
  const navClick = (v: AppView) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Modificador ou botão não-primário: sai do caminho e deixa o navegador
    // abrir em aba/janela nova. Sem isso o preventDefault mataria justamente
    // o comportamento que o link foi criado pra ter.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    go(v)
  }
  // Os toggles também recolhem o painel da Clara — sem isso os dois painéis
  // ficam abertos empilhados no mesmo espaço (achado do pente-fino 07-18).
  const toggleEnv = () => { setEnvOpen(o => !o); setSettingsOpen(false); claraVoice.dismissTurn() }
  const toggleSettings = () => { setSettingsOpen(o => !o); setEnvOpen(false); claraVoice.dismissTurn() }

  const itemClass = (isActive: boolean) => [
    'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    collapsed ? 'justify-center' : '',
    isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
    isActive ? 'neu-side-item neu-side-active' : 'neu-side-item',
  ].join(' ')

  // Painel ainda não liberado: mesma caixa, sem hover, sem foco, sem cursor de
  // link. `opacity-40` em vez de uma cor fixa pra continuar legível nos dois
  // temas (a sidebar é sempre escura, mas o token de cor não é).
  const itemClassBloqueado = [
    'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
    collapsed ? 'justify-center' : '',
    'neu-side-item text-muted-foreground opacity-40 cursor-not-allowed select-none',
  ].join(' ')
  const TITULO_BLOQUEADO = 'Em preparação — este painel ainda não foi liberado'

  const content = (
    <aside
      className={[
        // `dark` força o subtree em dark-mode: a sidebar é SEMPRE escura (mesmo
        // padrão do fin e do antigo header). O conteúdo é sempre light (app light-only).
        // z-40 > z-30 das colunas do inbox — a filipeta se projeta sobre a
        // lista de conversas e precisa vencer o empate de stacking.
        // `neu-side-float` descola a placa a partir de `lg`, DESDE QUE a raiz
        // tenha `.neu-stage` (App.tsx). Toda a geometria mora no CSS: aqui é só
        // o token. Ver o bloco SIDEBAR DESCOLADA no index.css.
        'dark relative z-[70] flex h-full flex-col bg-card border-r border-border shrink-0 transition-[width,box-shadow] duration-200 neu-side neu-side-float',
        // Sombra em degradê (mais escura na borda, clareando pra longe). Continua
        // aqui porque é o que vale nos dois casos em que a barra é RENTE: a gaveta
        // mobile e o `?neu=0`. Descolada, o .neu-side-float a substitui por uma
        // sombra ambiente — não apagar esta linha achando que virou morta.
        'shadow-[1px_0_2px_0_rgba(2,6,23,0.38),4px_0_10px_-2px_rgba(2,6,23,0.24),10px_0_22px_-4px_rgba(2,6,23,0.16),18px_0_36px_-8px_rgba(2,6,23,0.10)]',
        collapsed ? 'w-[68px]' : 'w-64',
      ].join(' ')}
    >
      {/* Recolher/expandir — "filipeta" (padrão dos 3 apps): círculo centrado NA
          linha da borda direita, metade dentro da sidebar, metade sobre o
          conteúdo. O acabamento está em `.neu-side .neu-side-flip` (index.css):
          o fio de luz da quina vira anel e contorna o arco, e um ::after cobre a
          metade de dentro com o fundo da sidebar — a filipeta lê como saliência
          da placa, não como disco pousado. Herda o dark.
          Não devolver `hover:bg-*` nem `shadow-*` aqui: os dois descolam o
          círculo da barra (ver o comentário longo no CSS). */}
      <button
        onClick={onToggleCollapse}
        title={collapsed ? 'Expandir' : 'Recolher'}
        className="hidden lg:flex absolute right-0 translate-x-1/2 top-1/2 -translate-y-1/2 z-50 items-center justify-center w-8 h-8 rounded-full bg-card text-muted-foreground hover:text-foreground transition-colors neu-side-flip"
      >
        {collapsed ? <FiChevronRight className="w-4 h-4" /> : <FiChevronLeft className="w-4 h-4" />}
      </button>
      {/* ── Marca (clica → volta pro painel WhatsApp, a "home") ── */}
      <button
        type="button"
        onClick={() => go(homeView)}
        title={`Ir para o painel ${crmViewLabel(homeView)}`}
        className="flex items-center justify-center min-h-[88px] px-3 py-3 select-none hover:opacity-80 transition-opacity"
      >
        {collapsed ? (
          <img src="/favicon.svg" alt="Distribuição Planalto" className="h-8 w-8" draggable={false} />
        ) : (
          /* Duas <img> + toggle pela classe .dark, mesmo padrão do AuthLogin:
             sem flicker e o navegador só baixa a visível. O GIF da matriz tinha
             contorno e sobrevivia nos dois temas com um arquivo só; a marca do
             Planalto é chapada, então uma versão preta sumiria na sidebar
             escura. */
          <>
            <img
              src="/planalto-animado-light.gif"
              alt="Distribuição Planalto"
              className="max-h-16 w-auto max-w-[90%] object-contain block dark:hidden"
              draggable={false}
            />
            <img
              src="/planalto-animado-dark.gif"
              alt="Distribuição Planalto"
              className="max-h-16 w-auto max-w-[90%] object-contain hidden dark:block"
              draggable={false}
            />
          </>
        )}
      </button>

      {/* ── Navegação + painéis (revelam no espaço livre abaixo) ── */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <nav className="flex-initial min-h-0 overflow-y-auto px-2 py-3 space-y-1">
          {/* Folha e grupo desenhados pela MESMA travessia, na ordem do
              NAV_TREE — que é a ordem do crm.twms. Antes o Recrutamento era um
              bloco à parte, escrito à mão depois do laço; com um segundo grupo
              isso duplicaria a marcação inteira, e é sempre a segunda cópia que
              esquece um detalhe (o filho bloqueado, o badge, o modo recolhido). */}
          {NAV_TREE.map((entrada) => {
            if (!ehGrupo(entrada)) {
              const { id, label, icon: Icon } = entrada
              if (!visibleNavItems.some(n => n.id === id)) return null
              // Painel não liberado vira <span>, não <a>: sem href não há
              // Ctrl+clique, botão do meio, "abrir em nova aba" nem foco por Tab —
              // desabilitar só no onClick deixaria todas essas portas abertas.
              if (!painelLiberado(id)) {
                return (
                  <span key={id} title={collapsed ? `${label}\n\n${TITULO_BLOQUEADO}` : TITULO_BLOQUEADO}
                        aria-disabled="true" className={itemClassBloqueado}>
                    <span className="relative shrink-0"><Icon className="w-[18px] h-[18px]" /></span>
                    {!collapsed && <span className="truncate flex-1 min-w-0 text-left">{label}</span>}
                  </span>
                )
              }
              const isActive = viewMode === id
              const dyn = badgeAtivo(id)
              const title = collapsed ? (dyn ? `${label}\n\n${dyn.tooltip}` : label) : undefined
              return (
                <a key={id} href={hrefFor(id)} onClick={navClick(id)} title={title} className={itemClass(isActive)}>
                  {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
                  <span className="relative shrink-0">
                    <Icon className="w-[18px] h-[18px]" />
                    {dyn && <span title={dyn.tooltip} className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ring-card cursor-help ${dyn.dotClass}`} />}
                  </span>
                  {!collapsed && <span className="truncate flex-1 min-w-0 text-left">{label}</span>}
                </a>
              )
            }

            const grupo = entrada
            if (!grupoVisivel(grupo)) return null
            const filhos = filhosDe(grupo)
            const aberto = !!abertos[grupo.id]
            const ativo = filhos.some(c => c.id === viewMode)
            const badge = badgeAtivo(grupo.id)
            const title = collapsed ? (badge ? `${grupo.label}\n\n${badge.tooltip}` : grupo.label) : undefined
            const GrupoIcon = grupo.icon
            // Recolhida, a linha do grupo NAVEGA (vai no 1º filho) — então é link,
            // igual aos outros itens. Expandida ela só abre/fecha a lista: continua
            // <button>, porque não tem endereço pra oferecer no botão direito.
            const groupInner = (
              <>
                <span className="relative shrink-0">
                  <GrupoIcon className="w-[18px] h-[18px]" />
                  {badge && <span title={badge.tooltip} className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ring-card cursor-help ${badge.dotClass}`} />}
                </span>
                {!collapsed && <span className="truncate flex-1 min-w-0 text-left">{grupo.label}</span>}
                {!collapsed && (
                  <span className="ml-auto">
                    {aberto ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                  </span>
                )}
              </>
            )
            // Grupo sem NENHUM filho liberado não abre nem navega: vira uma
            // linha cinza igual aos painéis bloqueados. Com filho liberado, a
            // versão recolhida tem que apontar pro primeiro LIBERADO — o
            // primeiro filho pode estar bloqueado, e o link cairia no guard.
            const primeiroLiberado = filhos.find(c => painelLiberado(c.id))
            if (!primeiroLiberado) {
              return (
                <div key={grupo.id} className="pt-1">
                  <span title={collapsed ? `${grupo.label}\n\n${TITULO_BLOQUEADO}` : TITULO_BLOQUEADO}
                        aria-disabled="true" className={itemClassBloqueado}>
                    <span className="relative shrink-0"><GrupoIcon className="w-[18px] h-[18px]" /></span>
                    {!collapsed && <span className="truncate flex-1 min-w-0 text-left">{grupo.label}</span>}
                  </span>
                </div>
              )
            }
            return (
              <div key={grupo.id} className="pt-1">
                {collapsed ? (
                  <a href={hrefFor(primeiroLiberado.id)} onClick={navClick(primeiroLiberado.id)} title={title} className={itemClass(ativo)}>
                    {groupInner}
                  </a>
                ) : (
                  <button onClick={() => alterna(grupo.id)} title={title} className={itemClass(false)}>
                    {groupInner}
                  </button>
                )}
                {!collapsed && aberto && (
                  <div className="mt-1 ml-4 pl-3 border-l border-border space-y-1">
                    {filhos.map(({ id, label, icon: Icon }) => {
                      if (!painelLiberado(id)) {
                        return (
                          <span key={id} title={TITULO_BLOQUEADO} aria-disabled="true" className={itemClassBloqueado}>
                            <Icon className="w-4 h-4 shrink-0" />
                            <span className="truncate flex-1 min-w-0 text-left">{label}</span>
                          </span>
                        )
                      }
                      const isActive = viewMode === id
                      return (
                        <a key={id} href={hrefFor(id)} onClick={navClick(id)} className={itemClass(isActive)}>
                          {isActive && <span className="absolute left-[-13px] top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="truncate flex-1 min-w-0 text-left">{label}</span>
                        </a>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* Sobra: os painéis ocupam só esse espaço livre, ancorados embaixo.
            Com o swap aberto, reserva altura mínima pro painel inteiro — senão,
            em tela baixa, o último ambiente fica cortado atrás do rodapé. */}
        <div className={[
          'flex-1 min-h-0 flex flex-col justify-end overflow-hidden transition-[min-height] duration-[550ms]',
          envOpen ? (collapsed ? 'min-h-[136px]' : 'min-h-[156px]') : claraPanelOpen ? 'min-h-[206px]' : '',
        ].join(' ')}>
          {user?.isAdmin && <ClaraTurnPanel open={claraPanelOpen} steps={claraVoice.turnSteps} onClose={claraVoice.dismissTurn} />}
          {user?.isAdmin && <SettingsRevealPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} collapsed={collapsed} onAction={onSettingsAction} />}
          {user?.isAdmin && <EnvSwitchPanel open={envOpen} onClose={() => setEnvOpen(false)} collapsed={collapsed} />}
        </div>
      </div>

      {/* ── Rodapé: avatar · tema · config(admin) · swap(admin) · sair ──
          Sem filete divisório (07/08/2026): na placa descolada os traços
          horizontais eram o que mais poluía. O respiro já separa marca, nav e
          rodapé. A vertical dos filhos de Recrutamento FICA: ali a linha não
          divide seções, mostra hierarquia. */}
      <div className="relative p-2 neu-side-foot">
        <div className={`flex ${collapsed ? 'flex-col items-center gap-1' : 'items-center gap-2 px-1'}`}>
          {/* Usuário: pra admin, o orbe da Clara TOMA O LUGAR do avatar (mesmo
              w-9 h-9) e é o botão liga/desliga da escuta ativa — cinza=
              desligado, amarelo=conectando/processando, verde=escutando,
              azul=falando (spec do zip no doc-storage; o WS /clara-ws exige
              admin no backend). Não-admin segue com a sigla. Nome do usuário
              continua no tooltip nos dois casos. */}
          <div className="flex items-center gap-2 min-w-0">
            {user?.isAdmin ? (() => {
              const orbMode: ClaraOrbMode =
                claraVoice.stage === 'listening' ? 'gravando'
                : claraVoice.stage === 'speaking' ? 'talking'
                : claraVoice.stage === 'declined' ? 'recusado'
                : claraVoice.stage === 'off' ? 'idle'
                : 'working' // connecting + working
              const orbTitle =
                claraVoice.stage === 'off' ? 'Ativar a escuta da Clara (chame pelo nome, ela responde por voz)'
                : claraVoice.stage === 'connecting' ? 'Conectando a escuta da Clara…'
                : claraVoice.stage === 'working' ? 'Clara processando… — clique para desativar'
                : claraVoice.stage === 'speaking' ? 'Clara falando… — clique para desativar'
                : claraVoice.stage === 'declined' ? 'Clara descartou o último comando (não entendeu ou julgou ruído)'
                : 'Clara escutando — clique para desativar'
              return (
                <button
                  onClick={claraVoice.toggle}
                  aria-label="Escuta ativa da Clara"
                  title={`${userLabel}\n${orbTitle}`}
                  className={`flex items-center justify-center w-9 h-9 shrink-0 transition-opacity ${
                    claraVoice.stage === 'off' ? 'opacity-60 hover:opacity-100' : ''
                  }`}
                >
                  <ClaraOrb mode={orbMode} className="w-9 h-9" />
                </button>
              )
            })() : (
              <div title={userLabel} className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary leading-none">{avatarLetter}</span>
              </div>
            )}
          </div>

          <div className={`flex items-center gap-1 ${collapsed ? 'flex-col' : 'ml-auto'}`}>
            {/* Lua: volta ao rodapé (2026-08-04) junto com o tema escuro em NEU.
                Herda a casca neu dos vizinhos pelo `.neu-side-foot button.rounded-lg`,
                então não precisa de CSS próprio. */}
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? 'Voltar ao tema claro' : 'Usar o tema escuro'}
              aria-label="Alternar tema"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              {theme === 'dark'
                ? <FiSun className="w-[18px] h-[18px]" />
                : <FiMoon className="w-[18px] h-[18px]" />}
            </button>
            {user?.isAdmin && (
              <button
                onClick={toggleSettings}
                title="Configurações"
                aria-label="Configurações"
                className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${settingsOpen ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'}`}
              >
                <FiSettings className="w-[18px] h-[18px]" />
              </button>
            )}
            {user?.isAdmin && (
              <button
                onClick={toggleEnv}
                title="Trocar de ambiente"
                aria-label="Trocar de ambiente"
                className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${envOpen ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'}`}
              >
                <FiRepeat className="w-[18px] h-[18px]" />
              </button>
            )}
            <button
              onClick={logout}
              title="Sair"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
            >
              <FiLogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  )

  return (
    <>
      {/* Desktop: fixa no fluxo */}
      <div className="hidden lg:flex">{content}</div>

      {/* Mobile: drawer sobre overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={onCloseMobile} />
          <div className="relative">{content}</div>
        </div>
      )}
    </>
  )
}

export { FiMenu }
